import React, { useMemo, useState } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import MonthlyTimeSeries from "../../components/graphs/MonthlyTimeSeries";
import { PRIMARY_COLOR } from "../../../styles/theme_constants";
import type { RankedUnit, ReportingOptions } from "../../../types/stationPerformance";
import { useFleetDistribution } from "../hooks/useFleetDistribution";
import { DISTRIBUTION_MEASURE_OPTIONS } from "../StationPerformance.consts";
import DefinitionContextBanner from "./DefinitionContextBanner";
import MeasureValueCell from "./MeasureValueCell";
import NeverComputedNotice from "./NeverComputedNotice";
import SpreadStatisticsPanel from "./SpreadStatisticsPanel";
import MoneyChangeWaterfall from "./MoneyChangeWaterfall";
import { useDecomposition } from "../hooks/useDecomposition";

interface FleetDistributionProps {
  month: string;
  options: ReportingOptions;
  onSelectUnit: (unitId: string) => void;
}

const TREND_MONTHS = 6;

const toMonthKey = (yearMonth: string) => {
  const [year, monthPart] = yearMonth.split("-");
  return { year: Number(year), month: Number(monthPart) };
};

const shiftMonth = (yearMonth: string, by: number) => {
  const { year, month } = toMonthKey(yearMonth);
  const d = new Date(year, month - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const TREND_EXPLANATION: Record<string, string> = {
  WIDENING: "The gap between strong and weak units is growing — worth asking what the leaders have that the others do not.",
  NARROWING: "The fleet is converging.",
  STABLE: "The gap is holding steady.",
};

/**
 * How far apart the fleet is, and whether that gap is moving (US6).
 *
 * The point of this screen is diagnostic rather than evaluative: it surfaces *which* units sit at the
 * extremes so someone can go and ask why: siting, local community, footfall, pricing. So both ends are
 * named and clickable; a distribution nobody can drill into is just a shape.
 *
 * The trend is charted on the coefficient of variation, not on raw spread, so a fleet that is simply
 * growing does not read as a widening gap.
 */
const FleetDistribution: React.FC<FleetDistributionProps> = ({ month, options, onSelectUnit }) => {
  const [measure, setMeasure] = useState("moneyCents");
  const trendFrom = useMemo(() => shiftMonth(month, -(TREND_MONTHS - 1)), [month]);
  const { report, initialLoading, neverComputed, error, fetchDistribution } = useFleetDistribution({
    month,
    measure,
    options,
    trendFrom,
  });

  // Memoised because the `?? []` fallback would otherwise hand a fresh array to the memos below on
  // every render, defeating them entirely.
  const trendSeries = useMemo(() => report?.spreadTrend?.series ?? [], [report]);
  const trendMonths = useMemo(() => trendSeries.map((p) => toMonthKey(p.yearMonth)), [trendSeries]);
  const trendFinals = useMemo(
    () => trendSeries.map((p) => report?.context?.monthClosure?.[p.yearMonth] !== "OPEN"),
    [trendSeries, report],
  );

  // The decomposition explains a change, so it needs the previous month too. Called unconditionally to
  // keep hook order stable; it renders only when it resolves.
  const previousMonth = useMemo(() => shiftMonth(month, -1), [month]);
  const { report: decomposition } = useDecomposition({
    from: previousMonth,
    to: month,
    options,
  });

  const isMoney = measure === "moneyCents" || measure === "moneyPerSessionCents";
  const measureLabel =
    DISTRIBUTION_MEASURE_OPTIONS.find((m) => m.value === measure)?.label ?? measure;

  if (initialLoading) {
    return (
      <div className="flex justify-content-center p-5">
        <ProgressSpinner />
      </div>
    );
  }
  if (neverComputed) return <NeverComputedNotice months={neverComputed} onComputed={fetchDistribution} />;
  if (error) return <Message severity="error" text={error} />;
  if (!report) return <Message severity="info" text="Nothing to show for this period." />;

  const rankedBody = (u: RankedUnit) => (
    <span>
      {isMoney
        ? (u.value / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" })
        : u.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}
    </span>
  );

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-wrap justify-content-between align-items-end gap-3">
        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">Measure</label>
          <Dropdown
            value={measure}
            options={DISTRIBUTION_MEASURE_OPTIONS}
            onChange={(e) => setMeasure(e.value)}
          />
        </div>
        <span className="text-color-secondary text-sm">
          {report.observationCount} active {options.unit === "SITE" ? "site" : "connector"}
          {report.observationCount === 1 ? "" : "s"} in {month}
        </span>
      </div>

      <DefinitionContextBanner context={report.context} />

      {report.observationCount < 2 && (
        <Message
          severity="info"
          text="Fewer than two active units in this period, so there is no spread to measure. Not zero — unmeasurable."
        />
      )}

      {decomposition && <MoneyChangeWaterfall report={decomposition} />}

      <Card>
        <SpreadStatisticsPanel
          title={`${measureLabel} across the fleet, ${month}`}
          statistics={report.statistics}
          observationCount={report.observationCount}
          format={isMoney ? "money" : "number"}
        />
      </Card>

      {report.spreadTrend && trendSeries.length >= 2 && (
        <Card>
          <MonthlyTimeSeries
            title="Spread across the fleet, month by month"
            months={trendMonths}
            finals={trendFinals}
            series={[{ label: "Variation (σ/mean)", color: PRIMARY_COLOR, data: trendSeries.map((p) => p.value) }]}
          />
          <div className="flex align-items-center gap-2 mt-2">
            <Tag
              value={report.spreadTrend.direction}
              severity={
                report.spreadTrend.direction === "WIDENING"
                  ? "warning"
                  : report.spreadTrend.direction === "NARROWING"
                    ? "success"
                    : "info"
              }
            />
            <span className="text-color-secondary text-sm">
              {TREND_EXPLANATION[report.spreadTrend.direction]}
            </span>
          </div>
        </Card>
      )}

      <div className="grid">
        <div className="col-12 md:col-6">
          <Card title="Strongest">
            <DataTable
              value={report.topUnits ?? []}
              dataKey="unitId"
              selectionMode="single"
              onSelectionChange={(e) => onSelectUnit((e.value as RankedUnit)?.unitId)}
              emptyMessage="Nothing to rank."
              size="small"
            >
              <Column field="rank" header="#" style={{ width: "4rem" }} />
              <Column field="stationName" header="Station" />
              <Column header={measureLabel} body={rankedBody} />
            </DataTable>
          </Card>
        </div>
        <div className="col-12 md:col-6">
          <Card title="Weakest">
            <DataTable
              value={report.bottomUnits ?? []}
              dataKey="unitId"
              selectionMode="single"
              onSelectionChange={(e) => onSelectUnit((e.value as RankedUnit)?.unitId)}
              emptyMessage="Nothing to rank."
              size="small"
            >
              <Column field="rank" header="#" style={{ width: "4rem" }} />
              <Column field="stationName" header="Station" />
              <Column header={measureLabel} body={rankedBody} />
            </DataTable>
          </Card>
        </div>
      </div>

      <span className="text-color-secondary text-sm">
        Click any station to open its detail. The variation figure is scatter relative to the mean, so a
        large and a small fleet month can be compared on consistency rather than on size —{" "}
        <MeasureValueCell measure={report.statistics?.coefficientOfVariation} decimals={3} /> here.
      </span>
    </div>
  );
};

export default FleetDistribution;
